import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Helper: Ajax cart
 * ------------------------------------------------------------------------------
 * An event based interface to support in communicating across components
 * that sends and retrieves responses from Ajax cart interactions.
 *
 * @namespace ajaxCart
 */

import * as cart from '@shopify/theme-cart';
import axios from 'axios';
import cssClasses from '~/helpers/cssClasses';
import { on, serialize } from '~/helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  container: '[js-ajax-cart="container"]',
  addToCart: '[js-ajax-cart="addToCart"]',
  clearCart: '[js-ajax-cart="clearCart"]',
  remove: '[js-ajax-cart="remove"]',
  lineItem: '[js-ajax-cart="lineItem"]',
  form: '[js-ajax-cart="form"]',
  cartCounter: '[js-ajax-cart="cartCounter"]',
  preorder: '[js-product-form="preorder"]'
};
/**
 * Export a new AjaxCart instance.
 * @param {object} config - Settings to apply across all Ajax calls to the cart.
 */

export default (config => {
  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    addToCart: [...document.querySelectorAll(selectors.addToCart)],
    cartCounter: document.querySelectorAll(selectors.cartCounter),
    clearCart: [...document.querySelectorAll(selectors.clearCart)],
    container: [...document.querySelectorAll(selectors.container)],
    preorder: document.querySelector(selectors.preorder)
  };
  /**
  * Initialise component.
  */

  function init() {
    setEventBusListeners();
    setEventListeners();
  }
  /**
   * Set EventBus listeners.
   */


  function setEventBusListeners() {
    /**
     * Create single endpoint for cart updates.
     */
    Frame.EventBus.listen(['AjaxCart:itemAdded', 'AjaxCart:itemRemoved', 'AjaxCart:itemUpdated', 'AjaxCart:cartCleared', 'AjaxCart:requestUpdate'], () => {
      handleCartUpdate();
    });
    /**
     * Conditionally listen for add on events.
     */

    if (theme.features.cartAddOn) {
      Frame.EventBus.listen(['AjaxCart:addOnItemAdded', 'AjaxCart:addOnItemRemoved'], () => handleCartUpdate());
    }
    /**
     * React to cart updates.
     */


    Frame.EventBus.listen('AjaxCart:updated', response => renderCartCounter(response));
    /**
     * Global helper function to add items to cart and trigger
     * responses and linked interactions.
     */

    Frame.EventBus.listen('AjaxCart:addItem', _ref => {
      let {
        id,
        options
      } = _ref;
      cart.addItem(id, options).then(response => {
        return Frame.EventBus.emit('AjaxCart:itemAdded', response);
      }).catch(error => {
        throw new Error(error);
      });
    });
    Frame.EventBus.listen('AjaxCart:removeItem', key => {
      cart.removeItem(key).then(response => {
        return Frame.EventBus.emit('AjaxCart:itemRemoved', response);
      }).catch(error => {
        throw new Error(error);
      });
    });
    /**
     * Global helper function to clear cart and note.
     */

    Frame.EventBus.listen('AjaxCart:clearCart', () => {
      cart.clearItems().then(() => cart.clearNote()).then(() => Frame.EventBus.emit('AjaxCart:cartCleared')).catch(error => {
        throw new Error('Could not clear cart', error);
      });
    });
  }
  /**
   * Handle cart updates.
   * - Creates single endpoint for all cart events.
   */


  function handleCartUpdate() {
    axios.get("/cart.js?no-cache=".concat(new Date().getTime())).then(response => {
      Frame.EventBus.emit('AjaxCart:updated', response.data);

      if (getItemCount(response.data) <= 0) {
        Frame.EventBus.emit('AjaxCart:cartEmpty');
      }
    }).catch(error => {
      throw new Error(error);
    });
  }
  /**
  * Set click events on buttons to post form data.
  */


  function setEventListeners() {
    nodeSelectors.addToCart.forEach(element => {
      on('click', element, event => handleAddToCart(event));
      on('keydown', element, event => handleAddToCart(event));
    });

    if (!nodeSelectors.clearCart) {
      return;
    }

    nodeSelectors.clearCart.forEach(element => {
      on('click', element, event => handleClearCart(event));
      on('keydown', element, event => handleClearCart(event));
    });
  }
  /**
   * Handle add to cart event and set loading state.
   * @param {Object} event - Click and key down event.
   */


  function handleAddToCart(event) {
    if (isKeyDownEvent(event) && !isKeyPressIsEnter(event)) {
      return;
    }

    event.preventDefault();
    const target = event.currentTarget;
    const targetInnerHtml = target.innerHTML;
    setLoadingState(target);
    axios.post('/cart/add.js', serialize(target.closest('form'))).then(response => {
      Frame.EventBus.emit('AjaxCart:itemAdded', response);
      setDefaultState(target, targetInnerHtml);

      if (config) {
        executeCallback();
      }

      return response;
    }).catch(error => {
      setDefaultState();
      throw new Error(error);
    });
  }
  /**
   * Check if key down event.
   * @param {Event} event - Keydown event object.
   * @returns {Boolean}
   */


  function isKeyDownEvent(event) {
    return event.type === 'keydown';
  }
  /**
   * Check if the return key is pressed.
   * @param {Event} event - Key code event.
   * @returns {Boolean}
   */


  function isKeyPressIsEnter(event) {
    return event.which === 13;
  }
  /**
   * Handle clear cart events.
   * @param {Event} event - Click/keydown event object.
   */


  function handleClearCart(event) {
    if (isKeyDownEvent(event) && !isKeyPressIsEnter(event)) {
      return;
    }

    event.preventDefault();
    Frame.EventBus.emit('AjaxCart:clearCart');
  }
  /**
   * Render cart counter.
   * @param {Object} response - Cart.js data.
   */


  function renderCartCounter(response) {
    const itemCount = getItemCount(response);
    nodeSelectors.cartCounter.forEach(element => {
      element.parentNode.classList.add('has-items');
      element.classList.remove(cssClasses.hidden);
      element.innerText = itemCount;

      if (itemCount <= 0) {
        element.classList.add(cssClasses.hidden);
        element.parentNode.classList.remove('has-items');
      }
    });
  }
  /**
   * Get cart counter and exclude add-on items.
   * @param {Object} response - Cart.js data.
   */


  function getItemCount(response) {
    return response.items.reduce((total, item) => {
      return item.product_type.toLowerCase() === 'add-on' ? 0 : total + item.quantity;
    }, 0);
  }
  /**
   * Set loading state.
   * @param {Object} target clicked add to cart button in DOM.
   */


  function setLoadingState(target) {
    [...nodeSelectors.addToCart].forEach(element => {
      if (target === element) {
        element.innerText = theme.strings.addingToCart;
      }
    });
  }
  /**
  * Reset the loading states back to default.
  * @param {Object} target clicked add to cart button in DOM.
  */


  function setDefaultState(target) {
    let innerHTML = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    [...nodeSelectors.addToCart].forEach(element => {
      if (target !== element) {
        return;
      }

      if (target) {
        target.blur();
      }

      if (innerHTML) {
        // Added to keep the right arrow after Add to cart.
        element.innerHTML = innerHTML;
        return;
      }

      element.innerText = theme.strings.addToCart;
    });
  }
  /**
   * Execute callback function defined in constructor.
   * @returns {Function|Boolean}
   */


  function executeCallback() {
    return typeof config.callback === 'function' ? config.callback() : false;
  }
  /**
   * Public interface.
   */


  return Object.freeze({
    init
  });
});